import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import type { SapEmploymentValues } from '@hcc/types';
import { useCurrentUser } from '../../../hooks/useAuth';
import { Button } from '../../../components/ui/Button';
import { EmploymentFieldsGrid } from '../../../components/sap/EmploymentFieldsGrid';
import { api, ApiError } from '../../../lib/api';

/**
 * Role-level SAP employment defaults, edited inline in the role editor.
 * Every person in the role inherits these unless overridden on their
 * SAP tab. Renders nothing for non-admins (the SAP endpoints are
 * ADMIN-only, stricter than the roles admin itself).
 */
export function SapRoleDefaultsCard({ roleId }: { roleId: string }) {
  const { data: user } = useCurrentUser();
  const queryClient = useQueryClient();
  const isAdmin = !!user?.roles.some((r) => r === 'admin' || r === 'super_admin');

  const defaults = useQuery({
    queryKey: ['sap-role-defaults', roleId],
    queryFn: () =>
      api<{ sap_defaults: SapEmploymentValues }>(`/api/admin/sap/roles/${roleId}/defaults`),
    enabled: isAdmin,
  });

  const [draft, setDraft] = useState<SapEmploymentValues>({});
  useEffect(() => {
    if (defaults.data) setDraft(defaults.data.sap_defaults);
  }, [defaults.data]);

  const save = useMutation({
    mutationFn: () =>
      api(`/api/admin/sap/roles/${roleId}/defaults`, {
        method: 'PATCH',
        body: JSON.stringify({
          org_centre_id: draft.org_centre_id ?? null,
          job_id: draft.job_id ?? null,
          contract_type: draft.contract_type ?? null,
          weekly_hours: draft.weekly_hours ?? null,
          working_time_model: draft.working_time_model ?? null,
        }),
      }),
    onSuccess: () => {
      toast.success('SAP defaults saved');
      void queryClient.invalidateQueries({ queryKey: ['sap-role-defaults', roleId] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'save failed'),
  });

  if (!isAdmin) return null;

  return (
    <div className="mt-3 rounded-token-sm border border-line/10 bg-surface-2 p-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">SAP employment defaults</div>
          <div className="text-xs text-fg-dim">
            Applied to every starter in this role unless overridden on their SAP tab.
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<Save className="h-4 w-4" />}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Save defaults
        </Button>
      </div>
      <div className="mt-3">
        <EmploymentFieldsGrid draft={draft} onChange={setDraft} />
      </div>
    </div>
  );
}
